'use client';

import { useCallback, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';

import {
  Command,
  CommandInput,
  CommandList,
  CommandShortcut,
} from '@kit/ui/command';
import { Dialog, DialogContent, DialogTitle } from '@kit/ui/dialog';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { useBrand } from '~/components/brand-provider';
import {
  isCrossBrand,
  resolveCrossBrandHref,
} from '~/lib/routing/resolve-app-url';
import { GlobalSearchResults } from './global-search-results';
import type { GlobalSearchKind } from './global-search-service';
import { useGlobalSearchQuery } from './use-global-search';

interface GlobalSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Query to seed the overlay with when it opens — the top-bar input hands
   * off whatever the user already typed (see GlobalSearchProvider
   * `openWithQuery`). Empty string means "open blank".
   */
  initialQuery?: string;
}

/**
 * The Cmd+K overlay. Owns the search query hook and the brand-aware navigation.
 * Built from `Dialog` + `Command` directly (not `CommandDialog`) so the cmdk
 * filter can be disabled with `shouldFilter={false}`: results are server-ranked,
 * so cmdk must not re-filter — its default filter would hide every row, since
 * the item values are stable ids, not the query text.
 */
export function GlobalSearchDialog({
  open,
  onOpenChange,
  initialQuery = '',
}: GlobalSearchDialogProps) {
  const { t } = useTranslation('search');
  const router = useRouter();
  const brand = useBrand();
  const { query, setQuery, debouncedQuery, enabled, page, isLoading } =
    useGlobalSearchQuery();

  // Seed the overlay with the characters typed into the top-bar input.
  // Effect (not render-time state) because the seed arrives together with
  // the `open` flip from the provider.
  useEffect(() => {
    if (open && initialQuery) {
      setQuery(initialQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-seed only on open/seed change
  }, [open, initialQuery]);

  const close = useCallback(() => {
    onOpenChange(false);
    setQuery('');
  }, [onOpenChange, setQuery]);

  const navigate = useCallback(
    (href: string) => {
      close();
      // A result may live on the other brand surface (dancer/org profiles are
      // Ballee-apex). Route cross-brand hops through the authenticated handoff
      // (a hard navigation); same-brand paths stay fast client nav.
      if (isCrossBrand(href, brand)) {
        window.location.assign(resolveCrossBrandHref(href, brand));
        return;
      }
      router.push(href);
    },
    [brand, close, router],
  );

  const seeAll = useCallback(
    (kind: GlobalSearchKind) => {
      navigate(`/search?q=${encodeURIComponent(debouncedQuery)}&type=${kind}`);
    },
    [debouncedQuery, navigate],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'flex flex-col gap-0 overflow-hidden p-0',
          // Mobile: full-screen search surface (Gojek/Instacart pattern), not
          // a floating mid-screen box. `inset-0` on the fixed DialogContent
          // fills the viewport (scrollbar-safe, no w-screen) and resets Radix's
          // centered translate.
          'max-sm:inset-0 max-sm:h-dvh max-sm:max-h-dvh max-sm:w-auto max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none max-sm:border-0',
          // Desktop: centered command palette.
          'sm:max-w-[560px]',
        )}
      >
        <DialogTitle className="sr-only">
          <Trans i18nKey="search:title" />
        </DialogTitle>
        <Command
          shouldFilter={false}
          className="[&_[cmdk-group-heading]]:text-muted-foreground flex min-h-0 flex-1 flex-col [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-2"
        >
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={t('placeholder')}
          />
          <CommandList className="min-h-0 flex-1 sm:max-h-[400px] sm:flex-none">
            <GlobalSearchResults
              query={debouncedQuery}
              enabled={enabled}
              isLoading={isLoading}
              page={page}
              onNavigate={navigate}
              onSeeAll={seeAll}
            />
          </CommandList>
          {/* Keyboard hints are desktop-only — meaningless on a touch device. */}
          <div className="text-muted-foreground hidden items-center justify-between border-t px-3 py-2 text-xs sm:flex">
            <Trans i18nKey="search:footer.navigate" />
            <div className="flex items-center gap-2">
              <CommandShortcut>↵</CommandShortcut>
              <CommandShortcut>esc</CommandShortcut>
            </div>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
